import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import '../App.css';
import API_BASE_URL from '../apiConfig';

const GET_ASSESSMENT_API_URL = `${API_BASE_URL}/assessment`;
const SUBMIT_ASSESSMENT_API_URL = `${API_BASE_URL}/submit-assessment`;
const START_ASSESSMENT_API_URL = (token) => `${API_BASE_URL}/assessment/${token}/start`;

// CONFIG: 15 Minutes in seconds
const TIME_LIMIT_SECONDS = 30 * 60; 

const SUBMIT_MESSAGES = [
  "Securely submitting answers...",
  "AI is analyzing your responses...",
  "Checking against grading rubrics...",
  "Calculating technical score...",
  "Finalizing report..."
];

function CandidateTestPage() {
  const { linkToken } = useParams();
  
  // --- STATE ---
  const [assessment, setAssessment] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  // Logic State
  const [timeLeft, setTimeLeft] = useState(TIME_LIMIT_SECONDS);
  const [loadingText, setLoadingText] = useState("Loading Assessment...");
  const [isMobile, setIsMobile] = useState(false); 
  
  // Consent State
  const [hasStarted, setHasStarted] = useState(false); 
  const [checkTerms, setCheckTerms] = useState(false);
  const [checkMonitoring, setCheckMonitoring] = useState(false);

  // Spy State
  const [startTime, setStartTime] = useState(null); 
  const [focusLossCount, setFocusLossCount] = useState(0); 
  const [pasteCounts, setPasteCounts] = useState({}); 

  // --- 1. INITIAL SETUP ---
  useEffect(() => {
    if (window.innerWidth <= 768) setIsMobile(true);
  }, []);

  // --- 2. PREVENT ACCIDENTAL REFRESH ---
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasStarted && !isSubmitted) {
        e.preventDefault();
        e.returnValue = ''; 
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasStarted, isSubmitted]);

  // --- 3. FETCH ASSESSMENT & SYNC TIMER (THE FIX) ---
  useEffect(() => {
    const fetchAssessment = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${GET_ASSESSMENT_API_URL}/${linkToken}`);
        const data = response.data;
        setAssessment(data);
        
        if (data.status === 'Completed') {
            setIsSubmitted(true); 
        } 
        // --- NEW: SERVER-SIDE TIMER SYNC ---
        else if (data.started_at) {
            // The user already started this test previously!
            const serverStartTime = new Date(data.started_at).getTime();
            const now = Date.now();
            const elapsedSeconds = Math.floor((now - serverStartTime) / 1000);
            const remaining = TIME_LIMIT_SECONDS - elapsedSeconds;

            if (remaining <= 0) {
                // Time expired while they were gone
                setIsSubmitted(true); 
            } else {
                // Resume where they left off
                setTimeLeft(remaining);
                setStartTime(serverStartTime); 
                setHasStarted(true); // Auto-skip the lobby
            }
        }
        // -----------------------------------

        setError(null);
      } catch (err) {
        console.error("Error fetching assessment:", err);
        setError("Could not load assessment. The link may be invalid or expired.");
      } finally {
        setLoading(false);
      }
    };
    fetchAssessment();
  }, [linkToken]);

  // --- 4. SPY: TRACK FOCUS LOSS ---
  useEffect(() => {
    const handleFocusLoss = () => {
      if (hasStarted && !isSubmitted) {
          console.log("Focus lost! 🕵️");
          setFocusLossCount(prev => prev + 1);
      }
    };
    window.addEventListener("blur", handleFocusLoss);
    return () => window.removeEventListener("blur", handleFocusLoss);
  }, [hasStarted, isSubmitted]); 

  // --- 5. TIMER LOGIC ---
  useEffect(() => {
    if (!hasStarted || isSubmitted) return;

    if (timeLeft === 0) {
        handleSubmit(); 
        return;
    }

    const timerId = setInterval(() => {
        setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timerId);
  }, [hasStarted, isSubmitted, timeLeft]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  // --- 6. LOADING MESSAGE CYCLE ---
  useEffect(() => {
    let interval = null;
    if (loading && assessment && !isSubmitted) { 
        let i = 0;
        setLoadingText(SUBMIT_MESSAGES[0]);
        interval = setInterval(() => {
            i = (i + 1) % SUBMIT_MESSAGES.length;
            setLoadingText(SUBMIT_MESSAGES[i]);
        }, 2000);
    } else if (loading && !assessment) {
        setLoadingText("Loading Assessment...");
    }
    return () => clearInterval(interval);
  }, [loading, assessment, isSubmitted]);


  // --- HANDLERS ---
  const handleAnswerChange = (questionId, value) => {
    setAnswers({ ...answers, [questionId]: value });
  };

  const handlePaste = (questionId) => {
    setPasteCounts(prev => ({ ...prev, [questionId]: (prev[questionId] || 0) + 1 }));
  };

  const handleStartTest = async () => {
    // --- NEW: TELL SERVER TO START TIMER ---
    try {
        await axios.post(START_ASSESSMENT_API_URL(linkToken));
        setHasStarted(true);
        setStartTime(Date.now()); 
    } catch (err) {
        console.error("Failed to start server timer", err);
        alert("Could not start assessment. Please check your connection.");
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    // Safety check: ensure startTime exists (it might be synced from server)
    const effectiveStartTime = startTime || (Date.now() - ((TIME_LIMIT_SECONDS - timeLeft) * 1000));
    
    const timeTakenSeconds = Math.min(Math.floor((Date.now() - effectiveStartTime) / 1000), TIME_LIMIT_SECONDS);

    const formattedAnswers = assessment.questions.map(q => ({
      question_id: q.id,
      answer_text: answers[q.id] || "",
      focus_loss_count: focusLossCount, 
      paste_count: pasteCounts[q.id] || 0, 
      time_taken_seconds: timeTakenSeconds 
    }));

    const requestData = {
      link_token: linkToken, 
      answers: formattedAnswers
    };

    try {
      const response = await axios.post(SUBMIT_ASSESSMENT_API_URL, requestData);
      
      // --- FIX IS HERE: Update the state with the server's response ---
      setAssessment(response.data); 
      // ---------------------------------------------------------------
      
      setIsSubmitted(true);
    } catch (error) {
      console.error("Error:", error);
      // Optional: Check if error is 400 (already completed)
      if (error.response && error.response.data.detail === "Assessment already completed.") {
          setIsSubmitted(true); // Treat as success if they double-clicked
      } else {
          alert("An error occurred while submitting. Please check your connection.");
      }
    } finally {
      setLoading(false);
    }
  };

  // --- RENDER STATES ---

  if (loading) {
    return (
      <div className="App">
        <header className="App-header">
          <div className="loader-container">
            <div className="spinner"></div>
            <div className="loading-text-dynamic">{loadingText}</div>
          </div>
        </header>
      </div>
    );
  }

  // --- EXPIRED / COMPLETED STATE HANDLING ---
  // If we loaded the assessment and saw it was completed or expired, show success screen immediately
  if (isSubmitted) {
    return (
      <div className="App" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <header className="App-header" style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div className="form-container" style={{ textAlign: 'center', padding: '40px', maxWidth: '500px' }}>
            {/* Logic: If assessment exists and status is completed, show success. 
                If status is Pending but isSubmitted is true, it means Time Expired on load. */}
            <div style={{fontSize: '3rem', marginBottom: '15px'}}>
                {assessment?.status === 'Completed' ? '✅' : '⏳'}
            </div>
            <h2 style={{ marginBottom: '15px' }}>
                {assessment?.status === 'Completed' ? 'Assessment Submitted!' : 'Time Expired'}
            </h2>
            <p style={{ fontSize: '1.1rem', color: '#555', lineHeight: '1.6' }}>
              {assessment?.status === 'Completed' 
                ? "Thank you for completing the assessment. The hiring team has been notified."
                : "The time limit for this assessment has expired."}
              <br/><br/>
              <strong>You may safely close this window now.</strong>
            </p>
          </div>
        </header>
        <footer className="footer"><p>&copy; {new Date().getFullYear()} LuxerAssess</p></footer>
      </div>
    );
  }

  if (error) return <div className="App"><header className="App-header"><h2 className="error-message">{error}</h2></header></div>;

  // --- LOBBY SCREEN (Detailed Legal Version) ---
  if (!hasStarted) {
    return (
      <div className="App" style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <header className="App-header" style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '40px' }}>
          <div className="form-container" style={{textAlign: 'left', maxWidth: '600px', width: '90%'}}>
            <h2 style={{textAlign: 'center', marginBottom: '20px'}}>Candidate Assessment</h2>
            
            <div style={{backgroundColor: '#f1f8ff', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #cce5ff'}}>
                <p style={{marginTop: 0}}>
                    <strong>Position:</strong> <span style={{textTransform: 'capitalize'}}>{assessment.job_title || 'N/A'}</span>
                </p>
                <p style={{marginBottom: 0}}><strong>Questions:</strong> {assessment.questions.length}</p>
                <p style={{marginBottom: 0, marginTop: '5px'}}><strong>Time Limit:</strong> 30 Minutes</p>
            </div>

            <h3>⚠️ Assessment Rules & Disclosures</h3>
            <ul style={{lineHeight: '1.6', color: '#444', marginBottom: '20px', paddingLeft: '20px'}}>
                <li>This assessment is timed. <strong>The timer begins when you click "I Understand & Begin Test".</strong></li>
                <li>If the timer reaches zero, your answers will be submitted automatically.</li>
                <li style={{color: '#d32f2f'}}><strong>Do Not Refresh:</strong> Refreshing or closing this window will result in the permanent loss of your answers.</li>
                <li><strong>No AI Assistance:</strong> You must answer using your own knowledge. Use of ChatGPT, Gemini, Copilot, or similar tools is prohibited.</li>
                <li><strong>Integrity Monitoring:</strong> Leaving this tab is monitored and reported.</li>
                <li><strong>Paste Detection:</strong> Copying and pasting large blocks of text is flagged.</li>
            </ul>

            {isMobile && (
                <div style={{backgroundColor: '#fff3cd', color: '#856404', padding: '10px', borderRadius: '5px', marginBottom: '20px', border: '1px solid #ffeeba'}}>
                    <strong>Mobile Detected:</strong> We strongly recommend using a desktop for coding questions.
                </div>
            )}

            <div style={{marginBottom: '15px', display: 'flex', alignItems: 'flex-start', gap: '10px'}}>
                <input 
                    type="checkbox" 
                    id="checkTerms" 
                    checked={checkTerms} 
                    onChange={(e) => setCheckTerms(e.target.checked)} 
                    style={{marginTop: '5px', cursor: 'pointer', transform: 'scale(1.2)'}} 
                />
                <label htmlFor="checkTerms" style={{fontSize: '0.9rem', color: '#555', cursor: 'pointer'}}>
                    I verify that I will answer these questions truthfully without AI assistance. I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" style={{color: '#0052cc'}}>Terms of Service</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{color: '#0052cc'}}>Privacy Policy</a>.
                </label>
            </div>

            <div style={{marginBottom: '20px', display: 'flex', alignItems: 'flex-start', gap: '10px'}}>
                <input 
                    type="checkbox" 
                    id="checkMonitoring" 
                    checked={checkMonitoring} 
                    onChange={(e) => setCheckMonitoring(e.target.checked)} 
                    style={{marginTop: '5px', cursor: 'pointer', transform: 'scale(1.2)'}} 
                />
                <label htmlFor="checkMonitoring" style={{fontSize: '0.9rem', color: '#555', cursor: 'pointer'}}>
                    <strong>Explicit Consent:</strong> I understand that my browser behavior (focus loss, pasting) will be monitored and reported to the recruiter for integrity verification.
                </label>
            </div>

            <button 
                className="generate-button" 
                onClick={handleStartTest} 
                disabled={!checkTerms || !checkMonitoring} 
                style={{
                    width: '100%', 
                    marginTop: '10px',
                    opacity: (checkTerms && checkMonitoring) ? 1 : 0.6, 
                    cursor: (checkTerms && checkMonitoring) ? 'pointer' : 'not-allowed'
                }}
            >
                I Understand & Begin Test
            </button>
            
            <div style={{marginTop: '20px', textAlign: 'center', fontSize: '0.8rem', color: '#888'}}>
                Powered by LuxerAssess
            </div>
          </div>
        </header>
      </div>
    );
  }

  // --- ACTIVE TEST SCREEN ---
  return (
    <div className="App" style={{minHeight: '100vh', backgroundColor: '#f4f7f6'}}>
        <div style={{
            position: 'fixed', top: 0, left: 0, width: '100%',
            backgroundColor: timeLeft < 60 ? '#d32f2f' : '#0052cc', 
            color: 'white',
            padding: '10px', textAlign: 'center', fontWeight: 'bold', fontSize: '1.1rem',
            boxShadow: '0 2px 5px rgba(0,0,0,0.2)', zIndex: 1000,
            transition: 'background-color 0.3s'
        }}>
            Time Remaining: {formatTime(timeLeft)}
        </div>

        <div className="App-header" style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', 
            paddingTop: '80px', 
            paddingBottom: '40px'
        }}>
            <div className="form-container" style={{maxWidth: '800px', width: '90%'}}> 
            <h2 style={{borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '20px'}}>
                {assessment.job_title}
            </h2>
            
            <div style={{
                marginBottom: '20px', 
                padding: '15px', 
                backgroundColor: '#fff3cd', 
                border: '1px solid #ffeeba', 
                borderRadius: '4px', 
                fontSize: '0.9rem', 
                color: '#856404',
                textAlign: 'left',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
            }}>
                <div>
                    🔴 <strong>Proctoring Active:</strong> Do not switch tabs or copy-paste text. Events are logged.
                </div>
                <div>
                    ⚠️ <strong>Do Not Refresh:</strong> Reloading or closing this page will wipe your answers immediately.
                </div>
            </div>
                        
            {assessment.questions.map((q, index) => (
                <div key={q.id} className="result-card question-card" style={{marginBottom: '30px'}}>
                <h4 style={{color: '#444'}}>Question {index + 1} of {assessment.questions.length}</h4> 
                
                <p className="question-text no-select" style={{whiteSpace: 'pre-wrap', lineHeight: '1.6'}}>
                    {q.question_text}
                </p>
                
                <textarea
                    className="answer-textarea" 
                    rows="10"
                    placeholder="Type your answer here..."
                    value={answers[q.id] || ""}
                    onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                    onPaste={() => handlePaste(q.id)}
                    disabled={loading}
                    style={{
                        width: '100%', padding: '10px', borderRadius: '4px', 
                        border: '1px solid #ccc', fontFamily: 'monospace', fontSize: '1rem'
                    }}
                />
                </div>
            ))}

            <button className="generate-button" onClick={handleSubmit} disabled={loading} style={{width: '100%', padding: '15px', fontSize: '1.1rem'}}>
                {loading ? "Submitting..." : "Submit Assessment"}
            </button>
            </div>
        </div>
    </div>
  );
}

export default CandidateTestPage;